import { Box, Button, Image } from '@chakra-ui/react';
import { MediaImage } from 'iconoir-react';
import { parseAsString, useQueryState } from 'nuqs';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface CoPubliciteThumbnailProps {
  thumbnailUrl: string | null;
  username: string;
  /**
   * When set, clicking (or pressing Enter/Space on) the thumbnail opens the
   * shared mention modal by setting the `previewMention` URL param, mirroring
   * MentionThumbnailPreviewSurface. The eye action opens the same detail pane;
   * its inline view now carries the full paid performance.
   */
  mentionId?: string;
}

/**
 * Square media preview for a co-publicite row. Falls back to a neutral
 * placeholder tile (no shadow, border + bg contrast only) when the boosted
 * mention has no hosted thumbnail. When `mentionId` is provided the tile becomes
 * a keyboard-accessible button that opens the default mention detail.
 */
export function CoPubliciteThumbnail({
  thumbnailUrl,
  username,
  mentionId,
}: CoPubliciteThumbnailProps) {
  const media = !thumbnailUrl ? (
    <Box
      boxSize="48px"
      overflow="hidden"
      rounded="sm"
      bg="gray.100"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="gray.400"
      data-testid="co-publicite-thumbnail-placeholder"
    >
      <MediaImage width={22} height={22} />
    </Box>
  ) : (
    <Image
      src={thumbnailUrl}
      alt={username}
      boxSize="48px"
      objectFit="cover"
      rounded="sm"
      bg="gray.100"
      data-testid="co-publicite-thumbnail"
    />
  );

  if (!mentionId) {
    return media;
  }

  return <ClickableThumbnail mentionId={mentionId}>{media}</ClickableThumbnail>;
}

interface ClickableThumbnailProps {
  mentionId: string;
  children: ReactNode;
}

/**
 * Opens the shared mention detail pane via `previewMention`, mirroring
 * MentionThumbnailPreviewSurface. Keyboard-accessible: Enter/Space activate it.
 */
function ClickableThumbnail({ mentionId, children }: ClickableThumbnailProps) {
  const { t } = useTranslation();
  const [, setPreviewMentionId] = useQueryState('previewMention', parseAsString);

  const open = () => {
    void setPreviewMentionId(mentionId);
  };

  return (
    <Button
      type="button"
      aria-label={t('mentions.preview.openLabel')}
      variant="plain"
      minW={0}
      width="100%"
      p={0}
      border={0}
      bg="transparent"
      display="block"
      textAlign="initial"
      cursor="pointer"
      height="auto"
      onClick={open}
      data-testid="co-publicite-thumbnail-button"
    >
      {children}
    </Button>
  );
}
